import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import applicationService from '../../services/applicationService';
import jobService from '../../services/jobService';
import './JobApplications.css';

// Helper function to get resume URL
const getResumeUrl = (resumePath) => {
  if (!resumePath) return '';

  // If it's already a full URL (http/https), return as is
  if (resumePath.startsWith('http://') || resumePath.startsWith('https://')) {
    return resumePath;
  }

  // For local files, construct the URL
  const baseUrl = process.env.REACT_APP_API_URL.replace('/api/v1', '');
  const filePath = resumePath.startsWith('/') ? resumePath : `/${resumePath}`;
  return `${baseUrl}${filePath}`;
};

const JobApplications = () => {
  const { jobId } = useParams();
  const [applications, setApplications] = useState([]);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [interviewData, setInterviewData] = useState({
    date: '',
    time: '',
    location: '',
    type: 'in-person',
    notes: ''
  });

  useEffect(() => {
    fetchJobDetails();
    fetchApplications();
  }, [jobId, filter]);

  const fetchJobDetails = async () => {
    try {
      const response = await jobService.getJobById(jobId);
      setJob(response.data);
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error('Failed to fetch job details');
    }
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = {
        job: jobId,
        status: filter !== 'all' ? filter : undefined
      };

      const response = await applicationService.getJobApplications(jobId,params);
      setApplications(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to fetch applications');
      setLoading(false);
    }
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      await applicationService.updateApplicationStatus(applicationId, newStatus);
      toast.success(`Application ${newStatus}`);
      fetchApplications();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update application status');
    }
  };

  const handleScheduleInterview = (application) => {
    setSelectedApplication(application);
    setShowModal(true);
  };

  const handleInterviewSubmit = async (e) => {
    e.preventDefault();
    try {
      await applicationService.scheduleInterview(selectedApplication._id, interviewData);
      toast.success('Interview scheduled successfully');
      setShowModal(false);
      setInterviewData({ date: '', time: '', location: '', type: 'in-person', notes: '' });
      fetchApplications();
    } catch (error) {
      console.error('Error scheduling interview:', error);
      toast.error('Failed to schedule interview');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      reviewed: 'badge-info',
      shortlisted: 'badge-primary',
      interviewed: 'badge-secondary',
      accepted: 'badge-success',
      rejected: 'badge-danger',
      withdrawn: 'badge-secondary'
    };
    return badges[status] || 'badge-secondary';
  };

  const statusConfig = {
    pending:     { label: 'Pending',     color: 'orange', icon: 'hourglass_empty' },
    reviewed:    { label: 'Reviewed',    color: 'blue',   icon: 'visibility' },
    shortlisted: { label: 'Shortlisted', color: 'purple', icon: 'star' },
    interviewed: { label: 'Interviewed', color: 'teal',   icon: 'video_camera_front' },
    offered:     { label: 'Offered',     color: 'green',  icon: 'workspace_premium' },
    accepted:    { label: 'Accepted',    color: 'green',  icon: 'check_circle' },
    rejected:    { label: 'Rejected',    color: 'red',    icon: 'cancel' },
  };

  const getCfg = (s) => statusConfig[s] || { label: s, color: 'gray', icon: 'description' };

  // Horizontal Status Pipeline Component
  const HorizontalStatusPipeline = ({ statusHistory, currentStatus }) => {
    const statusOrder = ['pending', 'reviewed', 'shortlisted', 'interviewed', 'offered', 'accepted', 'rejected'];
    const pipelineSteps = statusOrder.filter(status =>
      statusHistory.some(h => h.status === status) || status === currentStatus
    );

    return (
      <div className="horizontal-pipeline">
        {pipelineSteps.map((status, index) => {
          const historyItem = statusHistory.find(h => h.status === status);
          const isCompleted = historyItem || status === currentStatus;
          const isCurrent = status === currentStatus;
          const cfg = getCfg(status);

          return (
            <React.Fragment key={status}>
              <div className={`horizontal-step ${isCompleted ? 'completed' : 'pending'} ${isCurrent ? 'current' : ''}`}>
                {isCurrent ? (
                  <span className="material-symbols-outlined horizontal-icon current-star">star</span>
                ) : isCompleted ? (
                  <span className="material-symbols-outlined horizontal-icon">check_circle</span>
                ) : (
                  <span className="material-symbols-outlined horizontal-icon">radio_button_unchecked</span>
                )}
                <span className="horizontal-label">{cfg.label}</span>
              </div>
              {index < pipelineSteps.length - 1 && (
                <div className={`horizontal-connector ${isCompleted ? 'completed' : 'pending'}`}></div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const getInitials = (name) =>
    name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

  const tabs = [
    { key: 'all',         label: 'All',         count: applications.length },
    { key: 'pending',     label: 'Pending',      dot: 'orange' },
    { key: 'reviewed',    label: 'Reviewed',     dot: 'blue' },
    { key: 'shortlisted', label: 'Shortlisted',  dot: 'purple' },
    { key: 'interviewed', label: 'Interviewed',  dot: 'teal' },
    { key: 'offered',     label: 'Offered',      dot: 'green' },
    { key: 'accepted',    label: 'Accepted',     dot: 'green' },
    { key: 'rejected',    label: 'Rejected',     dot: 'red' },
  ];

  if (loading) {
    return (
      <div className="emp-job-application ja-container">
        <div className="ja-loading"><div className="ja-spinner"></div><p>Loading applications…</p></div>
      </div>
    );
  }

  return (
    <div className="emp-job-application ja-container">
      {/* Header */}
      <div className="ja-header">
        <div className="ja-header-left">
          <Link to="/employer/applications" className="ja-back-btn">
            <span className="material-symbols-outlined">arrow_back</span>
          </Link>
          <div>
            <h1 className="ja-title">{job ? job.title : 'Job Applications'}</h1>
            <p className="ja-subtitle">
              {job
                ? <>{job.company?.name} &bull; {job.location?.city?.name || 'Remote'} &bull; {applications.length} applicant{applications.length !== 1 ? 's' : ''}</>
                : 'View and manage candidate applications'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="ja-tabs-wrap">
        <div className="ja-tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              className={`ja-tab ${filter === t.key ? 'active' : ''}`}
              onClick={() => setFilter(t.key)}
            >
              {t.dot && <span className={`ja-tab-dot ${t.dot}`}></span>}
              {t.label}
              {t.count !== undefined && <span className="ja-tab-count">{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {applications.length === 0 ? (
        <div className="ja-empty">
          <span className="material-symbols-outlined">inbox</span>
          <h3>No applications found</h3>
          <p>{filter === 'all' ? 'Applications will appear here once candidates apply' : `No ${filter} applications`}</p>
        </div>
      ) : (
        <div className="ja-list">
          {applications.map((app) => {
            const cfg = getCfg(app.status);
            return (
              <div key={app._id} className="ja-card">
                {/* Card Top */}
                <div className="ja-card-top">
                  <div className="ja-avatar">{getInitials(app.candidate?.name)}</div>
                  <div className="ja-card-info">
                    <div className="ja-card-name-row">
                      <h3>{app.candidate?.name}</h3>
                      <span className={`ja-status-badge ${cfg.color}`}>
                        <span className="material-symbols-outlined">{cfg.icon}</span>
                        {cfg.label}
                      </span>
                      {app.statusHistory && app.statusHistory.length > 0 && (
                        <HorizontalStatusPipeline
                          statusHistory={app.statusHistory}
                          currentStatus={app.status}
                        />
                      )}
                    </div>
                    <div className="ja-card-meta">
                      <span><span className="material-symbols-outlined">email</span>{app.candidate?.email}</span>
                      {app.candidate?.phone && (
                        <span><span className="material-symbols-outlined">phone</span>{app.candidate.phone}</span>
                      )}
                      <span><span className="material-symbols-outlined">calendar_today</span>Applied {new Date(app.createdAt).toLocaleDateString()}</span>
                      {app.expectedSalary && (
                        <span><span className="material-symbols-outlined">currency_rupee</span>${app.expectedSalary.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Cover Letter */}
                {app.coverLetter && (
                  <div className="ja-cover-letter">
                    <div className="ja-cover-letter-label">
                      <span className="material-symbols-outlined">description</span>Cover Letter
                    </div>
                    <p>{app.coverLetter.length > 280 ? `${app.coverLetter.substring(0, 280)}…` : app.coverLetter}</p>
                  </div>
                )}

                {/* Interview Banner */}
                {app.interview && (
                  <div className="ja-interview-banner">
                    <span className="material-symbols-outlined">event</span>
                    <div>
                      <strong>Interview Scheduled</strong>
                      <span>{new Date(app.interview.date || app.interview.scheduledDate).toLocaleDateString()}</span>
                      {app.interview.type && <span> &bull; {app.interview.type}</span>}
                      {app.interview.location && <span> &bull; {app.interview.location}</span>}
                    </div>
                  </div>
                )}

                {/* Card Footer */}
                <div className="ja-card-footer">
                  {app.resume && (
                    <a href={getResumeUrl(app.resume)} target="_blank" rel="noopener noreferrer" className="ja-btn-resume">
                      <span className="material-symbols-outlined">picture_as_pdf</span>Resume
                    </a>
                  )}
                  <div className="ja-card-actions">
                    {app.status === 'pending' && (<>
                      <button className="ja-btn-primary" onClick={() => handleStatusChange(app._id, 'reviewed')}>
                        <span className="material-symbols-outlined">visibility</span>Start Review
                      </button>
                      <button className="ja-btn-danger" onClick={() => handleStatusChange(app._id, 'rejected')}>
                        <span className="material-symbols-outlined">cancel</span>Reject
                      </button>
                    </>)}
                    {app.status === 'reviewed' && (<>
                      <button className="ja-btn-purple" onClick={() => handleStatusChange(app._id, 'shortlisted')}>
                        <span className="material-symbols-outlined">star</span>Shortlist
                      </button>
                      <button className="ja-btn-danger" onClick={() => handleStatusChange(app._id, 'rejected')}>
                        <span className="material-symbols-outlined">cancel</span>Reject
                      </button>
                    </>)}
                    {app.status === 'shortlisted' && (<>
                      <button className="ja-btn-teal" onClick={() => handleScheduleInterview(app)}>
                        <span className="material-symbols-outlined">video_camera_front</span>Schedule Interview
                      </button>
                      <button className="ja-btn-danger" onClick={() => handleStatusChange(app._id, 'rejected')}>
                        <span className="material-symbols-outlined">cancel</span>Reject
                      </button>
                    </>)}
                    {app.status === 'interviewed' && (<>
                      <button className="ja-btn-success" onClick={() => handleStatusChange(app._id, 'offered')}>
                        <span className="material-symbols-outlined">workspace_premium</span>Make Offer
                      </button>
                      <button className="ja-btn-danger" onClick={() => handleStatusChange(app._id, 'rejected')}>
                        <span className="material-symbols-outlined">cancel</span>Reject
                      </button>
                    </>)}
                    {app.status === 'offered' && (<>
                      <button className="ja-btn-success" onClick={() => handleStatusChange(app._id, 'accepted')}>
                        <span className="material-symbols-outlined">check_circle</span>Mark Accepted
                      </button>
                      <button className="ja-btn-danger" onClick={() => handleStatusChange(app._id, 'rejected')}>
                        <span className="material-symbols-outlined">cancel</span>Reject
                      </button>
                    </>)}
                    {(app.status === 'accepted' || app.status === 'rejected') && (
                      <span className="ja-status-final">
                        <span className="material-symbols-outlined">{cfg.icon}</span>
                        {cfg.label}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Schedule Interview Modal */}
      {showModal && selectedApplication && (
        <div className="ja-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ja-modal" onClick={e => e.stopPropagation()}>
            <div className="ja-modal-header">
              <div>
                <h2>Schedule Interview</h2>
                <p>For {selectedApplication.candidate?.name}</p>
              </div>
              <button className="ja-modal-close" onClick={() => setShowModal(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <form onSubmit={handleInterviewSubmit} className="ja-modal-body">
              <div className="ja-form-row">
                <div className="ja-form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={interviewData.date}
                    onChange={(e) => setInterviewData({ ...interviewData, date: e.target.value })}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="ja-form-group">
                  <label>Time *</label>
                  <input
                    type="time"
                    value={interviewData.time}
                    onChange={(e) => setInterviewData({ ...interviewData, time: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="ja-form-group">
                <label>Interview Type *</label>
                <div className="ja-type-pills">
                  {[
                    { val: 'in-person', icon: 'person', label: 'In-Person' },
                    { val: 'video',     icon: 'videocam', label: 'Video Call' },
                    { val: 'phone',     icon: 'phone',  label: 'Phone Call' },
                  ].map(t => (
                    <button
                      key={t.val}
                      type="button"
                      className={`ja-type-pill ${interviewData.type === t.val ? 'active' : ''}`}
                      onClick={() => setInterviewData({ ...interviewData, type: t.val })}
                    >
                      <span className="material-symbols-outlined">{t.icon}</span>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ja-form-group">
                <label>Location / Meeting Link *</label>
                <input
                  type="text"
                  value={interviewData.location}
                  onChange={(e) => setInterviewData({ ...interviewData, location: e.target.value })}
                  placeholder={interviewData.type === 'video' ? 'https://meet.google.com/...' : 'Office address'}
                  required
                />
              </div>
              <div className="ja-form-group">
                <label>Notes for Candidate</label>
                <textarea
                  value={interviewData.notes}
                  onChange={(e) => setInterviewData({ ...interviewData, notes: e.target.value })}
                  placeholder="What should the candidate prepare or bring?"
                  rows="3"
                />
              </div>
              <div className="ja-modal-footer">
                <button type="button" className="ja-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="ja-btn-primary">
                  <span className="material-symbols-outlined">event</span>
                  Schedule Interview
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplications;
